import * as Api from '@ViewModels';
import { css, StyleDeclarationValue } from 'aphrodite';
import { IEmojiData } from 'emoji-picker-react';
import equal from 'fast-deep-equal';
import moment from 'moment';
import * as React from 'react';
import { Editor } from 'tinymce';
import { isCustomResourceSelector } from '../../../../extViewmodels/Utils';
import { Browser } from '../../../../models/Browser';
import { useErrorMessages, useUserSession } from '../../../../models/hooks/appStateHooks';
import { useEventLogging } from '../../../../models/Logging';
import { FileSendLinkPlaceholder } from '../../../../models/Token';
import {
	convertRawRichTextContentStateToRichContentEditorState,
	getRichContentMessageForTextingStep,
	getScheduleTimeOptions,
} from '../../../../models/UiUtils';
import { useAddTextingStepAttachmentsMutation } from '../../../../queries/Automation/mutations/attachments';
import {
	useAutomationTemplateStepAddConditionalMutation,
	useAutomationTemplateStepRemoveConditionalMutation,
} from '../../../../queries/Automation/mutations/steps';
import {
	Combobox,
	ComboboxContent,
	ComboboxEmpty,
	ComboboxInput,
	ComboboxItem,
	ComboboxItemIndicator,
	ComboboxList,
} from '../../../components-v2/Combobox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components-v2/Select';
import { brandPrimary } from '../../../styles/colors';
import { baseStyleSheet } from '../../../styles/styles';
import { AttachInputButton } from '../../AttachInputButton';
import { Checkbox } from '../../Checkbox';
import { ConfirmationDialog } from '../../ConfirmationDialog';
import type { ISelectOption } from '../../DeprecatedSelect';
import { InputFieldError } from '../../InputFieldError';
import { LoadingSpinner } from '../../LoadingSpinner';
import { RadioButton } from '../../RadioButton';
import { ISelectBoxOption } from '../../SelectBox';
import { DisclosureIcon } from '../../svgs/icons/DisclosureIcon';
import { WarningIcon } from '../../svgs/icons/WarningIcon';
import { TextCampaignEditor } from '../../TextCampaignEditor';
import { TextInput } from '../../TextInput';
import { EmojiPickerButton } from '../../texting/EmojiPickerButton';
import { MergeFieldsDropdown } from '../../texting/MergeFieldsDropdown';
import { EditAutomationStepEmailComposerModal } from '../EditAutomationStepEmailComposerModal';
import { BEHAVIOR_BRANCH_TRIGGER_TAG } from './schedulingConstants';
import { styleSheet } from './styles';

interface IAutomationStepCardScheduleProps {
	canEdit?: boolean;
	isBusy?: boolean;
	index: number;
	onRequestSetSchedule?(schedule: Api.IAutomationStepSchedule): void;
	schedule?: Api.IAutomationStepSchedule;
	step: Api.IAutomationStep;
	stepType: Api.AutomationStepType;
	trigger?: Api.IAutomationTrigger;
	triggerAnchorDescription?: string;
	restrictToAfterOrImmediately?: boolean;
	impersonationContext?: Api.IImpersonationContext;
	disableImmediately?: boolean;
}

export enum EScheduleAnchorOptions {
	Before = 'before',
	After = 'after',
	SameDay = 'sameDay',
}

export type AutomationStepScheduleAnchorQualifierSelectOption = ISelectOption<
	EScheduleAnchorOptions.Before | EScheduleAnchorOptions.After | EScheduleAnchorOptions.SameDay
>;

const stepTypesSupportingTimeOfDayInput = [
	Api.AutomationStepType.Email,
	Api.AutomationStepType.Texting,
	Api.AutomationStepType.HtmlNewsletter,
	Api.AutomationStepType.HandwrittenCard,
];

export const getTriggerDescription = (trigger?: Api.IAutomationTrigger) => {
	let triggerDescription: string = null;
	switch (trigger?._type) {
		case Api.AutomationTriggerType.ResourceSelector: {
			const t = trigger as Api.IResourceSelectorAutomationTrigger;
			switch (t.resourceSelector) {
				case Api.ResourceSelectorId.TurningXX:
				case Api.ResourceSelectorId.TurningXX2:
				case Api.ResourceSelectorId.TurningXX3:
				case Api.ResourceSelectorId.TurningXX4:
				case Api.ResourceSelectorId.TurningXX5:
				case Api.ResourceSelectorId.Turning72:
				case Api.ResourceSelectorId.Turning73:
				case Api.ResourceSelectorId.HappyBirthday:
				case Api.ResourceSelectorId.Turning65: {
					triggerDescription = 'birthday';
					break;
				}
				case Api.ResourceSelectorId.PolicyRenew: {
					triggerDescription = 'renewal';
					break;
				}
				case Api.ResourceSelectorId.FinancialReview: {
					triggerDescription = 'review';
					break;
				}
				case Api.ResourceSelectorId.Custom1:
				case Api.ResourceSelectorId.Custom2:
				case Api.ResourceSelectorId.Custom3:
				case Api.ResourceSelectorId.Custom4:
				case Api.ResourceSelectorId.Custom5:
				case Api.ResourceSelectorId.Custom6:
				case Api.ResourceSelectorId.Custom7:
				case Api.ResourceSelectorId.Custom8:
				case Api.ResourceSelectorId.Custom9:
				case Api.ResourceSelectorId.Custom10: {
					return 'custom trigger';
				}
				default: {
					triggerDescription = 'automation starts';
					break;
				}
			}
			break;
		}
		case Api.AutomationTriggerType.NewLead: {
			triggerDescription = 'new lead';
			break;
		}
		case Api.AutomationTriggerType.NewClient: {
			triggerDescription = 'new client';
			break;
		}
		case Api.AutomationTriggerType.NewDonor: {
			triggerDescription = 'new donor';
			break;
		}
		case Api.AutomationTriggerType.Tag: {
			triggerDescription = 'tag';
			break;
		}
		case Api.AutomationTriggerType.Meeting: {
			triggerDescription = 'meeting';
			break;
		}
		case Api.AutomationTriggerType.EventRegistration: {
			triggerDescription = 'event';
			break;
		}
		default: {
			triggerDescription = 'automation starts';
			break;
		}
	}

	return triggerDescription;
};

enum EHourDaysTypes {
	Hours = 'hours',
	Days = 'days',
	Minutes = 'minutes',
}

type TDaysHoursOptions = ISelectOption<EHourDaysTypes.Hours | EHourDaysTypes.Days | EHourDaysTypes.Minutes>;

export const getAutomationStepScheduleAnchorQualifierSelectOptions = (
	trigger?: Api.IAutomationTrigger,
	restrictToAfterOrImmediately = false
) => {
	let options: AutomationStepScheduleAnchorQualifierSelectOption[] = [];
	const addBefore =
		!restrictToAfterOrImmediately &&
		Api.VmUtils.Automations.triggers.canSetAutomationStepScheduleUsingNegativeNumberOfDays(trigger);
	if (addBefore) {
		options.push({
			dataContext: EScheduleAnchorOptions.Before,
			id: 'before',
			text: 'before',
		});
	}
	options = [
		...options,
		{
			dataContext: EScheduleAnchorOptions.After,
			id: 'after',
			text: 'after',
		},
	];
	if (
		trigger?._type !== Api.AutomationTriggerType.Meeting &&
		trigger?._type !== Api.AutomationTriggerType.EventRegistration
	) {
		options.push({
			dataContext: EScheduleAnchorOptions.SameDay,
			id: 'sameDay',
			text: 'same day as',
		});
	}
	return options;
};

export const getAutomationStepScheduleAnchorQualifierSelectedOptions = (
	options: AutomationStepScheduleAnchorQualifierSelectOption[],
	schedule?: Api.IAutomationStepSchedule,
	trigger?: Api.IAutomationTrigger,
	restrictToAfterOrImmediately = false
) => {
	if (restrictToAfterOrImmediately) {
		// Behavior branches without a root anchor support "after" and "same day as" but not "before".
		// numberOfDays === 0 indicates same day.
		if (schedule?.numberOfDays === 0) {
			return options.find(option => option.dataContext === EScheduleAnchorOptions.SameDay) || options[0];
		}
		return options.find(option => option.dataContext === EScheduleAnchorOptions.After) || options[0];
	}

	if (
		trigger?._type !== Api.AutomationTriggerType.Meeting &&
		trigger?._type !== Api.AutomationTriggerType.EventRegistration
	) {
		return options.filter(
			x =>
				x.dataContext ===
				(schedule?.numberOfDays < 0
					? EScheduleAnchorOptions.Before
					: schedule?.numberOfDays > 0
						? EScheduleAnchorOptions.After
						: EScheduleAnchorOptions.SameDay)
		)?.[0];
	}

	return options.filter(
		x =>
			x.dataContext ===
			((schedule?.numberOfDays || schedule?.numberOfHours || schedule?.numberOfMinutes) < 0
				? EScheduleAnchorOptions.Before
				: EScheduleAnchorOptions.After)
	)?.[0];
};

const getAutomationStepScheduleAnchorDaysHoursSelectOptions = (
	options: TDaysHoursOptions[],
	schedule?: Api.IAutomationStepSchedule
) => {
	return options.filter(
		x =>
			x.dataContext ===
			(schedule?.numberOfDays !== 0
				? EHourDaysTypes.Days
				: schedule?.numberOfMinutes !== 0
					? EHourDaysTypes.Minutes
					: EHourDaysTypes.Hours)
	)?.[0];
};

const getDayOfInputValueForNumberOfDays = (numberOfDays: number, defaultValue = 1) => {
	return (isNaN(numberOfDays) ? defaultValue : numberOfDays).toString();
};

const AutomationScheduleTimeOfDayInput = (props: IAutomationStepCardScheduleProps) => {
	const { schedule, onRequestSetSchedule, canEdit, stepType, impersonationContext } = props;
	const userSession = useUserSession();
	const scheduleOptions = React.useMemo<ISelectBoxOption<number>[]>(() => {
		// Texting has its own send-window config when set; otherwise it (and HtmlNewsletter,
		// HandwrittenCard) falls back to the account's email send intervals — matching the
		// "texting uses email holiday settings if not configured" convention used elsewhere
		// in the app (see CompanySettingsSection.tsx, EmailConfigurations/index.tsx).
		// When a CSM edits a customer's automation in impersonation context (without "log in as"),
		// userSession.account is the admin's own account — read features from impersonationContext
		// when present. getScheduleTimeOptions handles the same fallback for the email window.
		const effectiveAccount = impersonationContext?.account ?? userSession?.account;
		const sendEmailFeatures =
			stepType === Api.AutomationStepType.Texting ? effectiveAccount?.features?.texting?.sendIntervals : undefined;
		return getScheduleTimeOptions(0, userSession, sendEmailFeatures, moment(), impersonationContext, false, 15);
	}, [userSession, stepType, impersonationContext]);
	const onTimeOptionSelected = React.useCallback(
		(option: ISelectBoxOption<number>) => {
			const hours = Math.floor(option.value / 60);
			const minutes = option.value % 60;
			onRequestSetSchedule({
				...(schedule || {}),
				numberOfHours: hours,
				numberOfMinutes: minutes,
			});
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[onRequestSetSchedule, schedule]
	);
	const selectedTotalMinutes = !isNaN(schedule?.numberOfHours)
		? schedule.numberOfHours * 60 + (schedule?.numberOfMinutes || 0)
		: undefined;
	const selectedTime =
		selectedTotalMinutes != null ? scheduleOptions.find(x => x.value === selectedTotalMinutes) : undefined;

	return (
		<Combobox
			items={scheduleOptions}
			value={selectedTime ?? null}
			onValueChange={item => {
				const option = item as ISelectBoxOption<number> | null;
				if (option) {
					onTimeOptionSelected(option);
				}
			}}
			itemToStringLabel={item => (item as ISelectBoxOption<number>).title as string}
			isItemEqualToValue={(a, b) => (a as ISelectBoxOption<number>).value === (b as ISelectBoxOption<number>).value}
		>
			<ComboboxInput placeholder='Search time...' disabled={!canEdit} styles={[styleSheet.timeDropdownTrigger]} />
			<ComboboxContent side='top'>
				<ComboboxEmpty>No matching times</ComboboxEmpty>
				<ComboboxList>
					{(item: ISelectBoxOption<number>) => (
						<ComboboxItem key={item.value} value={item}>
							<ComboboxItemIndicator />
							{item.title as string}
						</ComboboxItem>
					)}
				</ComboboxList>
			</ComboboxContent>
		</Combobox>
	);
};

const daysHoursOptions: TDaysHoursOptions[] = [
	{
		dataContext: EHourDaysTypes.Days,
		id: 'days',
		text: 'day(s)',
	},
	{
		dataContext: EHourDaysTypes.Hours,
		id: 'hours',
		text: 'hr(s)',
	},
	{
		dataContext: EHourDaysTypes.Minutes,
		id: 'minutes',
		text: 'min(s)',
	},
];

type ScheduleMode = 'relative' | 'dayOfMonth';

interface IScheduleModeOption {
	id: ScheduleMode;
	text: string;
}

interface IScheduledMonthOption {
	id: string;
	text: string;
	month?: number;
}

interface IMonthlyRecurringDayOption {
	id: string;
	text: string;
	dayOfMonth: number;
}

const scheduleModeOptions: IScheduleModeOption[] = [
	{ id: 'relative', text: 'relative to the trigger' },
	{ id: 'dayOfMonth', text: 'on a day of the month' },
];

const scheduledMonthOptions: IScheduledMonthOption[] = [
	{ id: 'every-month', text: 'every month' },
	{ id: 'month-1', text: 'January', month: 1 },
	{ id: 'month-2', text: 'February', month: 2 },
	{ id: 'month-3', text: 'March', month: 3 },
	{ id: 'month-4', text: 'April', month: 4 },
	{ id: 'month-5', text: 'May', month: 5 },
	{ id: 'month-6', text: 'June', month: 6 },
	{ id: 'month-7', text: 'July', month: 7 },
	{ id: 'month-8', text: 'August', month: 8 },
	{ id: 'month-9', text: 'September', month: 9 },
	{ id: 'month-10', text: 'October', month: 10 },
	{ id: 'month-11', text: 'November', month: 11 },
	{ id: 'month-12', text: 'December', month: 12 },
];

const monthlyRecurringDayOptions: IMonthlyRecurringDayOption[] = [
	{ id: 'first', text: 'the 1st', dayOfMonth: 1 },
	{ id: 'fifteenth', text: 'the 15th', dayOfMonth: 15 },
	{ id: 'last-day', text: 'the last day', dayOfMonth: 31 },
];

const getScheduleMode = (schedule?: Api.IAutomationStepSchedule): ScheduleMode => {
	return schedule?.criteria === Api.AutomationStepScheduleCriteria.Scheduled ? 'dayOfMonth' : 'relative';
};

const getMaxScheduledDayOfMonth = (month?: number) => {
	switch (month) {
		case 2:
			return 29;
		case 4:
		case 6:
		case 9:
		case 11:
			return 30;
		default:
			return 31;
	}
};

const clampScheduledDayOfMonth = (day?: number, month?: number) => {
	const nextDay = Number.isFinite(day) ? Math.trunc(day) : 1;
	return Math.min(Math.max(nextDay, 1), getMaxScheduledDayOfMonth(month));
};

const getSelectedScheduledMonthOption = (schedule?: Api.IAutomationStepSchedule) => {
	return scheduledMonthOptions.find(option => option.month === schedule?.month) || scheduledMonthOptions[0];
};

const getSelectedMonthlyRecurringDayOption = (schedule?: Api.IAutomationStepSchedule) => {
	return (
		monthlyRecurringDayOptions.find(option => option.dayOfMonth === schedule?.dayOfMonth) ||
		monthlyRecurringDayOptions[0]
	);
};

const getSupportedMonthlyRecurringDayOfMonth = (dayOfMonth?: number) => {
	return (
		monthlyRecurringDayOptions.find(option => option.dayOfMonth === dayOfMonth)?.dayOfMonth ??
		monthlyRecurringDayOptions[0].dayOfMonth
	);
};

const DefaultAutomationStepScheduler = (
	props: IAutomationStepCardScheduleProps & { whenText?: string; selectedDueDateOption?: 'day' | 'none' }
) => {
	const {
		trigger,
		schedule,
		onRequestSetSchedule,
		selectedDueDateOption,
		stepType,
		canEdit,
		whenText,
		restrictToAfterOrImmediately,
		step,
	} = props;
	const isMeetingAutomation = trigger?._type === Api.AutomationTriggerType.Meeting;
	const isEventBaseAutomation =
		trigger?._type === Api.AutomationTriggerType.Meeting ||
		trigger?._type === Api.AutomationTriggerType.EventRegistration;
	const usesAnchoredSchedule = Api.VmUtils.Automations.triggers.triggerUsesRelativeToAnchorScheduleCriteria(trigger);
	const canUseScheduledDayOfMonthMode =
		!restrictToAfterOrImmediately &&
		!isEventBaseAutomation &&
		(!usesAnchoredSchedule || schedule?.criteria === Api.AutomationStepScheduleCriteria.Scheduled);

	const [selectedScheduleMode, setSelectedScheduleMode] = React.useState<ScheduleMode>(getScheduleMode(schedule));
	const [scheduledDayOfMonthInputValue, setScheduledDayOfMonthInputValue] = React.useState<string>(() =>
		getDayOfInputValueForNumberOfDays(clampScheduledDayOfMonth(schedule?.dayOfMonth, schedule?.month))
	);
	const [selectedDaysHoursOption, setSelectedDaysHoursOption] = React.useState<TDaysHoursOptions>(
		getAutomationStepScheduleAnchorDaysHoursSelectOptions(daysHoursOptions, schedule)
	);

	const scheduleCriteria = schedule?.criteria;
	React.useEffect(() => {
		setSelectedScheduleMode(
			scheduleCriteria === Api.AutomationStepScheduleCriteria.Scheduled ? 'dayOfMonth' : 'relative'
		);
	}, [scheduleCriteria]);

	React.useEffect(() => {
		if (selectedScheduleMode === 'dayOfMonth') {
			setScheduledDayOfMonthInputValue(
				getDayOfInputValueForNumberOfDays(clampScheduledDayOfMonth(schedule?.dayOfMonth, schedule?.month))
			);
		}
	}, [selectedScheduleMode, schedule?.dayOfMonth, schedule?.month]);

	// Sync selectedDaysHoursOption with schedule changes (e.g., after refresh)
	React.useEffect(() => {
		if (isEventBaseAutomation) {
			const expectedOption = getAutomationStepScheduleAnchorDaysHoursSelectOptions(daysHoursOptions, schedule);
			if (expectedOption && expectedOption.dataContext !== selectedDaysHoursOption?.dataContext) {
				setSelectedDaysHoursOption(expectedOption);
			}
		}
	}, [schedule, selectedDaysHoursOption?.dataContext, isEventBaseAutomation]);

	React.useEffect(() => {
		if (schedule?.numberOfDays !== 0 && selectedDueDateOption === 'day') {
			setSelectedQualifierOption(
				getAutomationStepScheduleAnchorQualifierSelectedOptions(
					options,
					schedule,
					trigger,
					restrictToAfterOrImmediately
				)
			);
			if (isEventBaseAutomation) {
				setSelectedDaysHoursOption(getAutomationStepScheduleAnchorDaysHoursSelectOptions(daysHoursOptions, schedule));
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selectedDueDateOption, restrictToAfterOrImmediately]);
	// #region AutomationScheduleDayOfInput callbacks and state
	const [options, setOptions] = React.useState<AutomationStepScheduleAnchorQualifierSelectOption[]>(
		getAutomationStepScheduleAnchorQualifierSelectOptions(trigger, restrictToAfterOrImmediately)
	);
	const [selectedQualifierOption, setSelectedQualifierOption] =
		React.useState<AutomationStepScheduleAnchorQualifierSelectOption>(
			getAutomationStepScheduleAnchorQualifierSelectedOptions(options, schedule, trigger, restrictToAfterOrImmediately)
		);

	React.useEffect(() => {
		const nextOptions = getAutomationStepScheduleAnchorQualifierSelectOptions(trigger, restrictToAfterOrImmediately);
		if (
			!equal(
				nextOptions.map(x => x.id),
				options.map(x => x.id)
			)
		) {
			setOptions(nextOptions);
			setSelectedQualifierOption(
				getAutomationStepScheduleAnchorQualifierSelectedOptions(
					nextOptions,
					schedule,
					trigger,
					restrictToAfterOrImmediately
				)
			);
		}
	}, [trigger, schedule, options, restrictToAfterOrImmediately]);
	// #endregion

	const buildScheduledDayOfMonthSchedule = React.useCallback(
		(overrides: Partial<Api.IAutomationStepSchedule> = {}): Api.IAutomationStepSchedule => {
			const month = Object.prototype.hasOwnProperty.call(overrides, 'month') ? overrides.month : schedule?.month;
			const dayOfMonth = clampScheduledDayOfMonth(overrides.dayOfMonth ?? schedule?.dayOfMonth, month);

			return {
				criteria: Api.AutomationStepScheduleCriteria.Scheduled,
				dayOfMonth,
				month,
				numberOfHours: overrides.numberOfHours ?? schedule?.numberOfHours ?? 9,
				numberOfMinutes: overrides.numberOfMinutes ?? schedule?.numberOfMinutes ?? 0,
			};
		},
		[schedule?.dayOfMonth, schedule?.month, schedule?.numberOfHours, schedule?.numberOfMinutes]
	);

	const onScheduleModeSelected = (mode: ScheduleMode) => {
		setSelectedScheduleMode(mode);

		if (mode === 'dayOfMonth') {
			onRequestSetSchedule?.(buildScheduledDayOfMonthSchedule());
			return;
		}

		const defaultSchedule = Api.VmUtils.Automations.steps.getDefaultScheduleForAutomationStep(stepType, trigger);
		onRequestSetSchedule?.({
			...defaultSchedule,
			dayOfMonth: undefined,
			month: undefined,
			scheduledStartDate: undefined,
			numberOfHours: schedule?.numberOfHours ?? defaultSchedule.numberOfHours,
			numberOfMinutes: schedule?.numberOfMinutes ?? defaultSchedule.numberOfMinutes,
		});
	};

	const onQualifierOptionSelected = (option: AutomationStepScheduleAnchorQualifierSelectOption) => {
		setSelectedQualifierOption(option);
		const nextSchedule: Api.IAutomationStepSchedule = {
			...(schedule || {}),
		};
		if (isEventBaseAutomation) {
			const days = Math.abs(schedule?.numberOfDays);
			const hours = Math.abs(schedule?.numberOfHours);
			const minutes = Math.abs(schedule?.numberOfMinutes);
			if (selectedDaysHoursOption.dataContext === EHourDaysTypes.Days) {
				const num = days || hours;
				nextSchedule.numberOfDays = getValueFromContext(option.dataContext, num);
			}
			if (selectedDaysHoursOption.dataContext === EHourDaysTypes.Hours) {
				const num = hours || days;
				nextSchedule.numberOfHours = getValueFromContext(option.dataContext, num);
			}
			if (selectedDaysHoursOption.dataContext === EHourDaysTypes.Minutes) {
				const num = minutes || days;
				nextSchedule.numberOfMinutes = getValueFromContext(option.dataContext, num);
			}
			if (option.dataContext === EScheduleAnchorOptions.Before && days === 0 && hours === 0 && minutes === 0) {
				nextSchedule.numberOfDays = -1;
				nextSchedule.numberOfHours = 0;
				nextSchedule.numberOfMinutes = 0;
			} else if (option.dataContext === EScheduleAnchorOptions.After && days === 0 && hours === 0 && minutes === 0) {
				nextSchedule.numberOfDays = 1;
				nextSchedule.numberOfHours = 0;
				nextSchedule.numberOfMinutes = 0;
			}
			onRequestSetSchedule?.(nextSchedule);
			return;
		}
		nextSchedule.numberOfDays =
			option.dataContext === EScheduleAnchorOptions.SameDay
				? 0
				: (option.dataContext === EScheduleAnchorOptions.Before ? -1 : 1) *
					(schedule?.numberOfDays === 0 ? 1 : Math.abs(schedule?.numberOfDays));
		onRequestSetSchedule?.(nextSchedule);
	};

	const numberOfDays = schedule?.numberOfDays || 0;
	const numberOfHours = schedule?.numberOfHours || 0;
	const numberOfMinutes = schedule?.numberOfMinutes || 0;
	const triggerDescription = props.triggerAnchorDescription || getTriggerDescription(trigger);
	const showNumberInput =
		numberOfDays !== 0 ||
		(numberOfHours !== 0 && isEventBaseAutomation) ||
		(numberOfMinutes !== 0 && isEventBaseAutomation) ||
		selectedQualifierOption.dataContext === EScheduleAnchorOptions.After ||
		selectedQualifierOption.dataContext === EScheduleAnchorOptions.Before;

	const handleOptionSelection = React.useCallback(
		(value: TDaysHoursOptions) => {
			setSelectedDaysHoursOption(value);
			const nextSchedule: Api.IAutomationStepSchedule = { ...(schedule || {}) };
			const days = Math.abs(schedule?.numberOfDays);
			const hours = Math.abs(schedule?.numberOfHours);
			const minutes = Math.abs(schedule?.numberOfMinutes);
			if (value.dataContext === EHourDaysTypes.Days) {
				const num = days || hours || minutes;
				nextSchedule.numberOfDays = getValueFromContext(selectedQualifierOption.dataContext, num);
				nextSchedule.numberOfHours = 0;
				nextSchedule.numberOfMinutes = 0;
			}
			if (value.dataContext === EHourDaysTypes.Hours) {
				const num = hours || days || minutes;
				nextSchedule.numberOfDays = 0;
				nextSchedule.numberOfHours = getValueFromContext(selectedQualifierOption.dataContext, num);
				nextSchedule.numberOfMinutes = 0;
			}
			if (value.dataContext === EHourDaysTypes.Minutes) {
				const num = minutes || hours || days;
				nextSchedule.numberOfDays = 0;
				nextSchedule.numberOfHours = 0;
				nextSchedule.numberOfMinutes = getValueFromContext(selectedQualifierOption.dataContext, num);
			}
			onRequestSetSchedule?.(nextSchedule);
		},
		[schedule, onRequestSetSchedule, selectedQualifierOption]
	);

	const onScheduledDayOfMonthInputBlur = (e: React.FocusEvent<HTMLInputElement>) => {
		const parsedValue = parseInt(e.target.value, 10);
		const nextDayOfMonth = clampScheduledDayOfMonth(parsedValue, schedule?.month);
		setScheduledDayOfMonthInputValue(nextDayOfMonth.toString());
		onRequestSetSchedule?.(buildScheduledDayOfMonthSchedule({ dayOfMonth: nextDayOfMonth }));
	};

	const onScheduledDayOfMonthInputFocus = React.useCallback((e: React.FocusEvent<HTMLInputElement>) => {
		const inputElement = e.target as HTMLInputElement;
		if (Browser.isMSEdge()) {
			setTimeout(() => {
				inputElement?.select();
			}, 0);
		} else {
			inputElement.select();
		}
	}, []);

	const onScheduledDayOfMonthInputChange = React.useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
		setScheduledDayOfMonthInputValue(e.target.value);
	}, []);

	const selectedScheduledMonthOption = getSelectedScheduledMonthOption(schedule);
	const selectedMonthlyRecurringDayOption = getSelectedMonthlyRecurringDayOption(schedule);
	const isScheduledForSpecificMonth = selectedScheduledMonthOption.month !== undefined;
	const daysTextOrDaysHoursSelect = isEventBaseAutomation ? (
		<DaysHoursSelect
			canEdit={canEdit}
			options={daysHoursOptions}
			selectedOption={selectedDaysHoursOption}
			onSelectedOption={handleOptionSelection}
		/>
	) : (
		<div> {` day${Math.abs(numberOfDays) > 1 || Math.abs(numberOfDays) === 0 ? 's ' : ''} `} </div>
	);

	const renderRelativeSchedulerRow = () => {
		return (
			<div className={css(styleSheet.schedulerRow)}>
				{!canUseScheduledDayOfMonthMode && (
					<span className={css(baseStyleSheet.flexShrink0, baseStyleSheet.pr1)}>{whenText || 'This step happens'}</span>
				)}
				{showNumberInput && (
					<>
						<AutomationScheduleDayOfInput
							{...props}
							trigger={trigger}
							selectedDueDateOption={selectedDueDateOption}
							initialValue={numberOfDays || 1}
							isEventBaseAutomation={isEventBaseAutomation}
							selectedDaysHoursOption={selectedDaysHoursOption}
							selectedQualifierOption={selectedQualifierOption}
						/>
						{daysTextOrDaysHoursSelect}
					</>
				)}
				<Select
					items={options.map(o => ({ value: o.id, label: o.text }))}
					value={selectedQualifierOption?.id}
					onValueChange={id => {
						const option = options.find(o => o.id === id);
						if (option) onQualifierOptionSelected(option);
					}}
					disabled={!canEdit}
				>
					<SelectTrigger aria-label='Relative schedule qualifier' styles={styleSheet.schedulerQualifierSelect}>
						<SelectValue />
					</SelectTrigger>
					<SelectContent>
						{options.map(opt => (
							<SelectItem key={opt.id} value={opt.id}>
								{opt.text}
							</SelectItem>
						))}
					</SelectContent>
				</Select>
				{!!triggerDescription && (
					<span className={css(styleSheet.schedulerTriggerText)}>{`the ${triggerDescription}`}</span>
				)}
			</div>
		);
	};

	const renderScheduledDayOfMonthRow = () => {
		const onScheduledMonthChanged = (id: string) => {
			const option = scheduledMonthOptions.find(monthOption => monthOption.id === id);
			if (!option) {
				return;
			}

			const parsedDayOfMonth = parseInt(scheduledDayOfMonthInputValue, 10);
			const nextDayOfMonth =
				option.month === undefined
					? getSupportedMonthlyRecurringDayOfMonth(parsedDayOfMonth)
					: clampScheduledDayOfMonth(parsedDayOfMonth, option.month);
			setScheduledDayOfMonthInputValue(nextDayOfMonth.toString());
			onRequestSetSchedule?.(
				buildScheduledDayOfMonthSchedule({
					dayOfMonth: nextDayOfMonth,
					month: option.month,
				})
			);
		};

		return (
			<div className={css(styleSheet.schedulerRow)}>
				{!canUseScheduledDayOfMonthMode && (
					<span className={css(baseStyleSheet.flexShrink0, baseStyleSheet.pr1)}>{whenText || 'This step happens'}</span>
				)}
				{isScheduledForSpecificMonth ? (
					<>
						<span>in</span>
						<Select
							items={scheduledMonthOptions.map(option => ({ value: option.id, label: option.text }))}
							value={selectedScheduledMonthOption.id}
							onValueChange={onScheduledMonthChanged}
							disabled={!canEdit}
						>
							<SelectTrigger aria-label='Scheduled month' styles={styleSheet.scheduledMonthSelect}>
								<SelectValue />
							</SelectTrigger>
							<SelectContent>
								{scheduledMonthOptions.map(option => (
									<SelectItem key={option.id} value={option.id}>
										{option.text}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
						<span>on day</span>
						<TextInput
							className={css(baseStyleSheet.removeChildInputSpinButton, styleSheet.dayOfTextInput)}
							disabled={!canEdit}
							inputClassName={css(styleSheet.dayOfInput)}
							inputId={`scheduled-day-of-month-input-${step?.id || 'new'}`}
							min={1}
							max={getMaxScheduledDayOfMonth(schedule?.month)}
							onBlur={onScheduledDayOfMonthInputBlur}
							onChange={onScheduledDayOfMonthInputChange}
							onFocus={onScheduledDayOfMonthInputFocus}
							step={1}
							type='number'
							value={scheduledDayOfMonthInputValue}
						/>
						<span>each year</span>
					</>
				) : (
					<>
						<Select
							items={scheduledMonthOptions.map(option => ({ value: option.id, label: option.text }))}
							value={selectedScheduledMonthOption.id}
							onValueChange={onScheduledMonthChanged}
							disabled={!canEdit}
						>
							<SelectTrigger aria-label='Scheduled month' styles={styleSheet.scheduledMonthSelect}>
								<SelectValue />
							</SelectTrigger>
							<SelectContent>
								{scheduledMonthOptions.map(option => (
									<SelectItem key={option.id} value={option.id}>
										{option.text}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
						<span>on</span>
						<Select
							items={monthlyRecurringDayOptions.map(option => ({ value: option.id, label: option.text }))}
							value={selectedMonthlyRecurringDayOption.id}
							onValueChange={id => {
								const option = monthlyRecurringDayOptions.find(dayOption => dayOption.id === id);
								if (!option) {
									return;
								}

								setScheduledDayOfMonthInputValue(option.dayOfMonth.toString());
								onRequestSetSchedule?.(buildScheduledDayOfMonthSchedule({ dayOfMonth: option.dayOfMonth }));
							}}
							disabled={!canEdit}
						>
							<SelectTrigger aria-label='Monthly recurring day' styles={styleSheet.scheduledRecurringDaySelect}>
								<SelectValue />
							</SelectTrigger>
							<SelectContent>
								{monthlyRecurringDayOptions.map(option => (
									<SelectItem key={option.id} value={option.id}>
										{option.text}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					</>
				)}
			</div>
		);
	};

	return (
		<div className={css(styleSheet.scheduler)}>
			{canUseScheduledDayOfMonthMode && (
				<div className={css(styleSheet.schedulerRow)}>
					<span className={css(baseStyleSheet.flexShrink0, baseStyleSheet.pr1)}>{whenText || 'This step happens'}</span>
					<Select
						items={scheduleModeOptions.map(option => ({ value: option.id, label: option.text }))}
						value={selectedScheduleMode}
						onValueChange={value => {
							if (value === 'relative' || value === 'dayOfMonth') {
								onScheduleModeSelected(value);
							}
						}}
						disabled={!canEdit}
					>
						<SelectTrigger aria-label='Schedule mode' styles={styleSheet.schedulerModeSelect}>
							<SelectValue />
						</SelectTrigger>
						<SelectContent>
							{scheduleModeOptions.map(option => (
								<SelectItem key={option.id} value={option.id}>
									{option.text}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
				</div>
			)}
			{selectedScheduleMode === 'dayOfMonth' ? renderScheduledDayOfMonthRow() : renderRelativeSchedulerRow()}
			{isMeetingAutomation &&
				selectedScheduleMode === 'relative' &&
				selectedQualifierOption.dataContext === EScheduleAnchorOptions.After && (
					<p className={`${css(styleSheet.noMargin)}`}>
						<strong>Pro Tip: </strong> Allow enough time after the meeting so you can cancel this automation if the
						recipient doesn&apos;t show up.
					</p>
				)}
			{!isEventBaseAutomation && stepTypesSupportingTimeOfDayInput.indexOf(stepType) >= 0 && (
				<div className={css(styleSheet.schedulerRow)}>
					at <AutomationScheduleTimeOfDayInput {...props} />
				</div>
			)}
		</div>
	);
};

interface IDaysHoursProps {
	canEdit: boolean;
	options: TDaysHoursOptions[];
	selectedOption: TDaysHoursOptions;
	onSelectedOption: (option: ISelectOption) => void;
}

const DaysHoursSelect: React.FC<IDaysHoursProps> = ({ canEdit, options, selectedOption, onSelectedOption }) => {
	return (
		<Select
			items={options.map(o => ({ value: o.id, label: o.text }))}
			value={selectedOption?.id}
			onValueChange={id => {
				const option = options.find(o => o.id === id);
				if (option) onSelectedOption(option);
			}}
			disabled={!canEdit}
		>
			<SelectTrigger styles={styleSheet.hoursDaysSelect}>
				<SelectValue />
			</SelectTrigger>
			<SelectContent>
				{options.map(opt => (
					<SelectItem key={opt.id} value={opt.id}>
						{opt.text}
					</SelectItem>
				))}
			</SelectContent>
		</Select>
	);
};

const getValueFromContext = (context: EScheduleAnchorOptions, num: number) => {
	return (context === EScheduleAnchorOptions.Before ? -1 : 1) * Math.abs(num);
};

const AutomationScheduleDayOfInput: React.FC<
	IAutomationStepCardScheduleProps & {
		initialValue: number;
		isEventBaseAutomation: boolean;
		selectedDaysHoursOption?: TDaysHoursOptions;
		selectedQualifierOption?: AutomationStepScheduleAnchorQualifierSelectOption;
		selectedDueDateOption?: 'day' | 'none';
	}
> = ({
	canEdit,
	initialValue,
	isEventBaseAutomation,
	onRequestSetSchedule,
	trigger,
	schedule,
	step,
	selectedDaysHoursOption,
	selectedQualifierOption,
	selectedDueDateOption,
	restrictToAfterOrImmediately,
}) => {
	const getScheduleValue = () => {
		if (isEventBaseAutomation && selectedDaysHoursOption) {
			if (selectedDaysHoursOption.dataContext === EHourDaysTypes.Hours) {
				return schedule?.numberOfHours ?? initialValue;
			}
			if (selectedDaysHoursOption.dataContext === EHourDaysTypes.Minutes) {
				return schedule?.numberOfMinutes ?? initialValue;
			}
			// EHourDaysTypes.Days falls through to numberOfDays below
		}
		return schedule?.numberOfDays ?? initialValue;
	};
	const [dayOfAutomationInputValue, setDayOfAutomationInputValue] = React.useState<string>(
		getDayOfInputValueForNumberOfDays(getScheduleValue())
	);

	React.useEffect(() => {
		if (schedule?.numberOfDays !== 0 && selectedDueDateOption === 'day') {
			setDayOfAutomationInputValue(getDayOfInputValueForNumberOfDays(schedule?.numberOfDays));
		} else if (
			!schedule?.numberOfDays &&
			selectedDueDateOption === 'none' &&
			isCustomResourceSelector(trigger?.resourceSelector)
		) {
			setDayOfAutomationInputValue(getDayOfInputValueForNumberOfDays(0));
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selectedDueDateOption]);
	const setScheduleNumberOfDays = (numberOfDays: number) => () => {
		if (isEventBaseAutomation) {
			const nextSchedule: Api.IAutomationStepSchedule = {
				...(schedule || {}),
			};
			if (selectedDaysHoursOption.dataContext === EHourDaysTypes.Days) {
				nextSchedule.numberOfDays = getValueFromContext(selectedQualifierOption.dataContext, numberOfDays);
				nextSchedule.numberOfHours = 0;
				nextSchedule.numberOfMinutes = 0;
			}
			if (selectedDaysHoursOption.dataContext === EHourDaysTypes.Hours) {
				nextSchedule.numberOfDays = 0;
				nextSchedule.numberOfHours = getValueFromContext(selectedQualifierOption.dataContext, numberOfDays);
				nextSchedule.numberOfMinutes = 0;
			}
			if (selectedDaysHoursOption.dataContext === EHourDaysTypes.Minutes) {
				nextSchedule.numberOfDays = 0;
				nextSchedule.numberOfHours = 0;
				nextSchedule.numberOfMinutes = getValueFromContext(selectedQualifierOption.dataContext, numberOfDays);
			}
			onRequestSetSchedule?.(nextSchedule);
			return;
		}

		onRequestSetSchedule?.({
			...(schedule || {}),
			numberOfDays,
		});
	};

	const onDayOfAutomationInputBlur = (e: React.FocusEvent<HTMLInputElement>) => {
		let value = parseInt(e.target.value, 10);
		if (!value || isNaN(value)) {
			value = 1;
		}
		const sign = restrictToAfterOrImmediately
			? 1
			: !isNaN(schedule?.numberOfDays) && schedule?.numberOfDays >= 0
				? 1
				: -1;
		setScheduleNumberOfDays(sign * value)();
		setDayOfAutomationInputValue('' + value);
	};

	const onDayOfInputFocus = React.useCallback((e: React.FocusEvent<HTMLInputElement>) => {
		const inputElement = e.target as HTMLInputElement;
		if (Browser.isMSEdge()) {
			setTimeout(() => {
				inputElement?.select();
			}, 0);
		} else {
			inputElement.select();
		}
	}, []);

	const onDayOfAutomationChanged = React.useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
		setDayOfAutomationInputValue(e.target.value);
	}, []);

	const days = Math.abs(parseInt(dayOfAutomationInputValue, 10));
	const stepId = step.id;

	return (
		<TextInput
			className={css(baseStyleSheet.removeChildInputSpinButton, styleSheet.dayOfTextInput)}
			disabled={!canEdit}
			inputClassName={css(styleSheet.dayOfInput)}
			inputId={`dayof-automation-input-${stepId}`}
			min={1}
			onBlur={onDayOfAutomationInputBlur}
			onChange={onDayOfAutomationChanged}
			onFocus={onDayOfInputFocus}
			step={1}
			type='number'
			value={isNaN(days) ? '' : days}
		/>
	);
};

const ActionItemAutomationScheduler: React.FC<
	IAutomationStepCardScheduleProps & {
		optionsStyles?: StyleDeclarationValue[];
		optionStyles?: StyleDeclarationValue[];
		onRenderDueDateOption?(disabled?: boolean): React.ReactNode;
	}
> = props => {
	const { schedule, canEdit, step, onRequestSetSchedule, optionsStyles = [], optionStyles = [], trigger } = props;
	const stepId = step.id;

	const isEventBased =
		trigger?._type === Api.AutomationTriggerType.Meeting ||
		trigger?._type === Api.AutomationTriggerType.EventRegistration;
	const [selectedDueDateOption, setSelectedDueDateOption] = React.useState<'day' | 'none'>(
		schedule != null ? 'day' : 'none'
	);
	const onDueDateOptionChanged =
		(_selectedDueDateOption: 'day' | 'none') => (e: React.ChangeEvent<HTMLInputElement>) => {
			if (e.target.checked) {
				let options: Api.IAutomationStepSchedule | null = null;
				setSelectedDueDateOption(_selectedDueDateOption);
				if (_selectedDueDateOption === 'day') {
					options = {
						numberOfDays: 0,
					};
					if (isEventBased) {
						options = {
							criteria: Api.AutomationStepScheduleCriteria.RelativeToAnchor,
							numberOfDays: -1,
						};
					}
					if (Api.VmUtils.Automations.triggers.triggerUsesRelativeToAnchorScheduleCriteria(trigger)) {
						options = {
							criteria: Api.AutomationStepScheduleCriteria.RelativeToAnchor,
							numberOfDays: -30,
						};
					}

					onRequestSetSchedule?.({ ...options });
					return;
				}
				onRequestSetSchedule?.(options);
			}
		};

	const canEditDaysOf = !!schedule;
	return (
		<>
			<p className={css(styleSheet.noMargin)}>
				Due date for action item (<em>this can be adjusted later</em>
				):
			</p>
			<div className={css(styleSheet.actionItemDueDateOptions, ...optionsStyles)}>
				<RadioButton
					checked={canEditDaysOf}
					className={css(...optionStyles)}
					disabled={!canEdit}
					id={`automation-action-item-step-due-date-input-${stepId}`}
					name={`automation-action-item-step-due-date-${stepId}`}
					onChange={onDueDateOptionChanged('day')}
				>
					<div className={css(styleSheet.actionItemDueDateOption)}>
						<DefaultAutomationStepScheduler
							selectedDueDateOption={selectedDueDateOption}
							{...props}
							canEdit={canEditDaysOf && canEdit}
							whenText='Due'
						/>
					</div>
				</RadioButton>
				<RadioButton
					checked={!canEditDaysOf}
					className={css(...optionStyles)}
					disabled={!canEdit}
					id={`automation-action-item-step-due-date-none-${stepId}`}
					name={`automation-action-item-step-due-date-${stepId}`}
					onChange={onDueDateOptionChanged('none')}
				>
					<span>No due date</span>
				</RadioButton>
			</div>
		</>
	);
};

const AutomationStepCardScheduleDayOf: React.FC<React.PropsWithChildren<IAutomationStepCardScheduleProps>> = props => {
	const {
		schedule,
		canEdit,
		stepType,
		onRequestSetSchedule,
		trigger,
		children,
		isBusy,
		restrictToAfterOrImmediately,
		disableImmediately,
	} = props;

	const isEventBased =
		trigger?._type === Api.AutomationTriggerType.Meeting ||
		trigger?._type === Api.AutomationTriggerType.EventRegistration;
	const setSchedule = React.useCallback(
		(scheduleToSet: Api.IAutomationStepSchedule) => () => {
			// Below is no longer applicable as we are allowing immediately for all steps
			// note: we hard code an index > 0 when not explicitly setting criteria
			// in order to prevent "getDefaultScheduleForAutomationStep" from returning Api.AutomationStepScheduleCriteria.Immediately
			const scheduleTrigger = restrictToAfterOrImmediately
				? ({
						_type: Api.AutomationTriggerType.Tag,
						tag: BEHAVIOR_BRANCH_TRIGGER_TAG,
					} as Api.ITagAutomationTrigger)
				: trigger;
			const defaultSchedule = Api.VmUtils.Automations.steps.getDefaultScheduleForAutomationStep(
				stepType,
				scheduleTrigger
			);
			onRequestSetSchedule?.({
				...(schedule || {}),
				...defaultSchedule,
				...(scheduleToSet || {}),
			});
		},
		[schedule, onRequestSetSchedule, trigger, stepType, restrictToAfterOrImmediately]
	);

	const supportsImmediate = Api.VmUtils.Automations.steps.supportsImmediatelyCriteriaForFirstStepInAutomation(stepType);
	const usesAnchoredSchedule = Api.VmUtils.Automations.triggers.triggerUsesRelativeToAnchorScheduleCriteria(trigger);
	if (disableImmediately || (!restrictToAfterOrImmediately && usesAnchoredSchedule) || !supportsImmediate) {
		return <div>{children}</div>;
	}

	return (
		<div>
			<div className={css(styleSheet.dayOfHorizontalLayout)}>
				{!schedule || schedule?.criteria === Api.AutomationStepScheduleCriteria.Immediately ? (
					<span>
						<span>This step should happen immediately.</span>
						&nbsp;
						{canEdit ? (
							<button
								className={css(baseStyleSheet.brandLink)}
								onClick={setSchedule({ numberOfDays: restrictToAfterOrImmediately ? 0 : isEventBased ? -1 : 30 })}
							>
								<span>Change</span>
							</button>
						) : null}
					</span>
				) : (
					children
				)}
			</div>
			{!!schedule && schedule.criteria !== Api.AutomationStepScheduleCriteria.Immediately && (
				<div className={css(styleSheet.dayOfDetails, styleSheet.dayOfDetailsRightAlign)}>
					<button
						className={css(baseStyleSheet.brandLink)}
						onClick={setSchedule({
							criteria: Api.AutomationStepScheduleCriteria.Immediately,
							numberOfDays: 0,
							numberOfHours: 0,
							numberOfMinutes: 0,
						})}
						disabled={isBusy}
					>
						<span className={css(styleSheet.dayOfDetailsText)}>
							{isBusy ? <LoadingSpinner type='tiny' /> : undefined} Change to &quot;Immediately&quot;
						</span>
					</button>
				</div>
			)}
		</div>
	);
};

export const AdvancedOptionsButton = ({ isCollapsed, onClick }: { isCollapsed: boolean; onClick: () => void }) => {
	return (
		<button className={css(baseStyleSheet.brandLink, styleSheet.advancedOptionsButton)} onClick={onClick}>
			<span>Advanced Options</span>
			<span className={css(styleSheet.advancedOptionsChevron)}>
				<DisclosureIcon
					type='chevron'
					className={css(
						styleSheet.advancedOptionsChevronIcon,
						!isCollapsed && styleSheet.advancedOptionsChevronIconFlipped
					)}
					fillColor={brandPrimary}
				/>
			</span>
		</button>
	);
};

export const AutomationStepCardSchedule: React.FC<IAutomationStepCardScheduleProps> = props => {
	const { stepType } = props;
	let scheduler: React.ReactNode;

	if (!scheduler) {
		switch (stepType) {
			case Api.AutomationStepType.ActionItem: {
				scheduler = <ActionItemAutomationScheduler {...props} />;
				break;
			}
			default: {
				scheduler = <DefaultAutomationStepScheduler {...props} />;
				break;
			}
		}
	}

	return <AutomationStepCardScheduleDayOf {...props}>{scheduler}</AutomationStepCardScheduleDayOf>;
};

type IAutomationStepCardInputFieldErrorProps = Partial<IAutomationStepCardSettingsProps>;

export const AutomationStepCardInputFieldError: React.FC<
	React.PropsWithChildren<IAutomationStepCardInputFieldErrorProps>
> = ({ step, children }) => {
	let errorMessage: string = null;
	if ((step as any)?.showRequiredFieldError && !(step as any)?.hasAllRequiredInfo) {
		switch (step._type as string) {
			case Api.AutomationStepType.Texting: {
				errorMessage = 'Please provide texting content.';
				break;
			}
			case Api.AutomationStepType.Email: {
				errorMessage = 'Please provide email content and description/name.';
				break;
			}
			case Api.AutomationStepType.HtmlNewsletter: {
				errorMessage = 'Please select an HTML newsletter template.';
				break;
			}
			case Api.AutomationStepType.ActionItem: {
				errorMessage = 'Please provide action item content.';
				break;
			}
			case Api.AutomationStepType.RemoveTag:
			case Api.AutomationStepType.AddTag: {
				errorMessage = 'Please provide a tag name.';
				break;
			}
			case Api.AutomationStepType.HandwrittenCard: {
				errorMessage = 'Please select a card template and fill out card content.';
				break;
			}
			default: {
				break;
			}
		}
	}
	return <InputFieldError errorMessage={errorMessage}>{children}</InputFieldError>;
};

interface IAutomationStepCardSettingsProps<T extends Api.IAutomationStep = Api.IAutomationStep> {
	index: number;
	onRequestClearErrorMessages?(): void;
	onRequestSave?(
		automationStep?: Api.IAutomationStep,
		schedule?: Api.IAutomationStepSchedule,
		loggingEventParams?: Api.IDictionary<string | number | boolean>
	): Promise<any>;
	readOnly?: boolean;
	step?: T;
	trigger?: Api.IAutomationTrigger;
	schedule?: Api.IAutomationStepSchedule;
	onStepChanged?: () => void;
	/** Callback to update the parent step data after a conditional step mutation. */
	updateStep?: (stepId: string, updater: (prev: Api.IAutomationStep) => Api.IAutomationStep) => void;
	/** Template ID for attachment API calls */
	templateId?: string;
	/** Step ID for attachment API calls */
	stepId?: string;
	/** Impersonation context for admin users editing customer automations */
	impersonationContext?: Api.IImpersonationContext;
	/** When provided, the ref's .current is set to a function that inserts HTML into the editor and returns the new full HTML */
	insertContentRef?: React.MutableRefObject<((content: string) => string) | null>;
	removeContentRef?: React.MutableRefObject<((symbolOrSymbols: string | string[]) => string | undefined) | null>;
	hasContentTokenRef?: React.MutableRefObject<(() => boolean) | null>;
	canSendFileLinks?: boolean;
	renderBeforeFallbackEmail?: React.ReactNode;
}

type AutomationStepCardTextingInputProps = IAutomationStepCardSettingsProps<Api.ITextingAutomationStep>;

export const AutomationStepCardTextingInput: React.FC<AutomationStepCardTextingInputProps> = props => {
	const userSession = useUserSession();
	const {
		onRequestSave,
		readOnly,
		onRequestClearErrorMessages,
		step,
		trigger,
		schedule,
		onStepChanged,
		updateStep,
		templateId,
		stepId,
		impersonationContext,
		insertContentRef,
		removeContentRef,
		hasContentTokenRef,
		canSendFileLinks,
		renderBeforeFallbackEmail,
	} = props;
	const [contentEditorState, setContentEditorState] = React.useState<Api.IRichContentEditorState>(
		convertRawRichTextContentStateToRichContentEditorState(step?.content)
	);
	const attachmentsToBeSent = React.useRef<Api.AttachmentsToBeUploadedViewModel<File>>(
		new Api.AttachmentsToBeUploadedViewModel(null, Api.TextMessagingMaxFileByteSize)
	).current;
	const contentEditorRef = React.useRef<Editor>(null);
	const isMountedRef = React.useRef(true);
	const suppressNextBlurSaveRef = React.useRef(false);
	const mergeFieldInsertedDuringOpenRef = React.useRef(false);
	const mergeFieldMenuCloseTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
	const { logEvent, logApiError } = useEventLogging('AutomationStepCardTextingInput');
	const errorMessages = useErrorMessages();
	const onEditorLoaded = React.useCallback(
		(editor?: Editor) => {
			contentEditorRef.current = editor;
			if (insertContentRef && editor) {
				insertContentRef.current = (content: string): string => {
					editor.focus();
					editor.insertContent(content);
					return editor.getContent();
				};
			}
			if (removeContentRef && editor) {
				removeContentRef.current = (symbolOrSymbols: string | string[]): string | undefined => {
					const symbols = Array.isArray(symbolOrSymbols) ? symbolOrSymbols : [symbolOrSymbols];
					const currentHtml = editor.getContent();
					if (!symbols.some(s => currentHtml.includes(s))) return undefined;
					const html = symbols.reduce((h, s) => h.split(s).join(''), currentHtml);
					editor.setContent(html);
					if (isMountedRef.current) {
						const content = Api.createRawRichTextContentStateWithHtmlStringValue(html);
						setContentEditorState(convertRawRichTextContentStateToRichContentEditorState(content));
					}
					return html;
				};
			}
			if (hasContentTokenRef && editor) {
				hasContentTokenRef.current = (): boolean => {
					const html = editor.getContent();
					const allSymbols = [FileSendLinkPlaceholder.symbol, ...(FileSendLinkPlaceholder.additionalSymbols ?? [])];
					return allSymbols.some(s => html.includes(s));
				};
			}
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[contentEditorRef]
	);
	const [showingSizeLimitWarning, setShowingSizeLimitWarning] = React.useState(false);

	React.useEffect(() => {
		isMountedRef.current = true;
		return () => {
			isMountedRef.current = false;
		};
	}, []);

	const addConditionalStepMutation = useAutomationTemplateStepAddConditionalMutation({
		onError: error => logApiError('AddConditionalStep-Error', error),
	});
	const removeConditionalStepMutation = useAutomationTemplateStepRemoveConditionalMutation({
		onError: error => logApiError('RemoveConditionalStep-Error', error),
	});
	const addAttachmentMutation = useAddTextingStepAttachmentsMutation({
		onSuccess: () => onStepChanged?.(),
		onError: error => {
			logApiError('AddTextingAttachment-Error', error);
			errorMessages.pushApiError(error);
		},
	});

	const getModel = React.useCallback(() => {
		const stepModel: Partial<Api.ITextingAutomationStep> = {
			...(step || {}),
			content: contentEditorState?.getRawRichTextContent(),
			name: 'Texting Step',
		};
		if (!templateId || !stepId) {
			stepModel.attachments = attachmentsToBeSent.attachments as unknown as Api.IFileAttachment[];
		}
		return stepModel;
	}, [contentEditorState, step, attachmentsToBeSent, templateId, stepId]);

	const save = React.useCallback(
		(stepToSave?: Partial<Api.ITextingAutomationStep>) => {
			if (step && onRequestSave) {
				const stepModel = stepToSave || getModel();
				const eventParams: any = {};
				eventParams.nameLength = stepModel.name?.length;
				eventParams.hasContent = contentEditorState?.hasContent();
				return onRequestSave(stepModel, schedule, eventParams);
			}
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[onRequestSave, contentEditorState, getModel, schedule]
	);

	const saveEditorHtmlContent = React.useCallback(
		async (
			contentHtmlStringValue: string,
			errorEventName: string,
			overrideFields?: Partial<Api.ITextingAutomationStep>
		) => {
			try {
				const content = Api.createRawRichTextContentStateWithHtmlStringValue(contentHtmlStringValue);
				const model = {
					...getModel(),
					content,
					...(overrideFields || {}),
				};
				if (isMountedRef.current) {
					setContentEditorState(convertRawRichTextContentStateToRichContentEditorState(content));
				}
				await save(model);
			} catch (err) {
				logApiError(errorEventName, err);
			}
		},
		[getModel, logApiError, save]
	);

	const saveEditorHtmlContentRef = React.useRef(saveEditorHtmlContent);
	React.useEffect(() => {
		saveEditorHtmlContentRef.current = saveEditorHtmlContent;
	}, [saveEditorHtmlContent]);

	const onFilesInputChanged = async (e: React.ChangeEvent<HTMLInputElement>) => {
		const file = e.currentTarget.files[0];
		if (!file) {
			return; // cancelled the file picker
		}

		if (file) {
			let fileSize = 0;
			(step?.attachments || []).forEach((x: Api.IFileAttachment) => {
				const updateSize = fileSize;
				fileSize = x.fileSize + updateSize;
			});
			if (file.size + fileSize > Api.TextMessagingMaxFileByteSize) {
				setShowingSizeLimitWarning(true);
				return;
			}
			try {
				attachmentsToBeSent.remove(attachmentsToBeSent.attachments);
				// Removes any attachments that are still in current ref
				attachmentsToBeSent.add([file]);
				if (templateId && stepId) {
					await addAttachmentMutation.mutateAsync({
						templateId,
						stepId,
						attachmentFiles: [file],
						impersonationContext,
					});
				}
			} catch (err) {
				logApiError('LocalImageUpload-Error', err);
				errorMessages.pushApiError(err);
			}
		}
	};

	// #region texting toolbar
	const onEmojiClicked = React.useCallback(
		async (_: React.MouseEvent<Element, MouseEvent>, data: IEmojiData) => {
			if (contentEditorRef.current) {
				contentEditorRef.current.insertContent(data.emoji);

				// get content and save because editor does not trigger on change event after insert
				const contentHtmlStringValue = contentEditorRef.current.getContent();
				await saveEditorHtmlContent(contentHtmlStringValue, 'InsertEmojiAndSave-Error');
			}
		},
		[saveEditorHtmlContent]
	);

	// #endregion

	// #region texting content
	React.useEffect(() => {
		setContentEditorState(convertRawRichTextContentStateToRichContentEditorState(step?.content));
	}, [step]);

	const onTextingContentEditorStateChanged = React.useCallback(
		(nextContent: Api.IRichContentEditorState) => {
			onRequestClearErrorMessages?.();
			setContentEditorState(nextContent);
		},
		[onRequestClearErrorMessages]
	);

	const onTextingContentEditorBlur = React.useCallback(() => {
		if (suppressNextBlurSaveRef.current) {
			return;
		}
		void save();
	}, [save]);

	const onMergeFieldsInteractionStart = React.useCallback(() => {
		suppressNextBlurSaveRef.current = true;
		if (mergeFieldMenuCloseTimerRef.current) {
			clearTimeout(mergeFieldMenuCloseTimerRef.current);
			mergeFieldMenuCloseTimerRef.current = null;
		}
	}, []);

	const onMergeFieldsOpenChange = React.useCallback(
		(open: boolean) => {
			if (mergeFieldMenuCloseTimerRef.current) {
				clearTimeout(mergeFieldMenuCloseTimerRef.current);
				mergeFieldMenuCloseTimerRef.current = null;
			}

			if (open) {
				suppressNextBlurSaveRef.current = true;
				mergeFieldInsertedDuringOpenRef.current = false;
				return;
			}

			mergeFieldMenuCloseTimerRef.current = setTimeout(() => {
				if (mergeFieldInsertedDuringOpenRef.current) {
					// Base UI can restore focus after item press; don't let that blur re-save stale pre-insert content.
					mergeFieldMenuCloseTimerRef.current = setTimeout(() => {
						suppressNextBlurSaveRef.current = false;
						mergeFieldInsertedDuringOpenRef.current = false;
						mergeFieldMenuCloseTimerRef.current = null;
					}, 100);
					return;
				}

				try {
					const contentHtmlStringValue = contentEditorRef.current?.getContent();
					if (contentHtmlStringValue !== undefined) {
						void saveEditorHtmlContent(contentHtmlStringValue, 'SaveTextingContentAfterMergeFieldsMenu-Error');
					} else {
						void save();
					}
				} catch (err) {
					logApiError('SaveTextingContentAfterMergeFieldsMenu-Error', err);
					void save();
				}
				suppressNextBlurSaveRef.current = false;
				mergeFieldInsertedDuringOpenRef.current = false;
				mergeFieldMenuCloseTimerRef.current = null;
			}, 0);
		},
		[logApiError, save, saveEditorHtmlContent]
	);

	const onMergeFieldInserted = React.useCallback(
		(contentHtmlStringValue: string) => {
			mergeFieldInsertedDuringOpenRef.current = true;
			void saveEditorHtmlContent(contentHtmlStringValue, 'InsertMergeFieldAndSave-Error');
		},
		[saveEditorHtmlContent]
	);
	// #endregion

	const onCloseSizeWarning = () => {
		setShowingSizeLimitWarning(false);
	};

	const fallbackEmailAutomationStep = step?.conditionalSteps?.[
		Api.AutomationStepOutcome.Error
	] as Api.IEmailAutomationStep;
	const [hasFallbackEmailChecked, setHasFallbackEmailChecked] = React.useState<boolean>(
		Boolean(fallbackEmailAutomationStep)
	);
	const [showingEmailFallbackComposer, setShowingEmailFallbackComposer] = React.useState<boolean>(false);
	const onShowEmailFallbackComposerClicked = () => {
		setShowingEmailFallbackComposer(true);
		onRequestClearErrorMessages?.();
	};
	const onEmailFallbackComposerModalRequestClose = (data?: Api.IEmailAutomationStep) => {
		if (data) {
			updateStep?.(step.id, prev => ({
				...prev,
				conditionalSteps: {
					...prev.conditionalSteps,
					[Api.AutomationStepOutcome.Error]: data,
				},
			}));
		}
		setShowingEmailFallbackComposer(false);
	};

	// Sync local checkbox state when the step's conditionalSteps changes externally
	React.useEffect(() => {
		setHasFallbackEmailChecked(Boolean(step?.conditionalSteps?.[Api.AutomationStepOutcome.Error]));
	}, [step?.conditionalSteps]);

	const onFallbackToEmailCheckboxChanged = async (e: React.ChangeEvent<HTMLInputElement>) => {
		if (!templateId || !step.id) {
			return;
		}

		try {
			if (e.target.checked) {
				setHasFallbackEmailChecked(true);
				const emailStep: Api.IEmailAutomationStep = {
					_type: 'EmailAutomationStep',
					name: 'Email Fallback Step',
					options: {},
					conditionalSchedule: {
						criteria: Api.AutomationStepScheduleCriteria.RelativeToAnchor,
						numberOfDays: 0,
						numberOfHours: 9,
						numberOfMinutes: 0,
					},
					schedule: {
						criteria: Api.AutomationStepScheduleCriteria.Conditional,
					},
				};
				logEvent('AddConditionalStep', {
					outcome: Api.AutomationStepOutcome.Error,
					parentStepId: step?.id,
					type: emailStep._type,
				});
				const result = await addConditionalStepMutation.mutateAsync({
					templateId,
					stepId: step.id,
					outcome: Api.AutomationStepOutcome.Error,
					stepType: Api.AutomationStepType.Email,
					step: emailStep,
					impersonationContext,
				});
				// The API returns the updated PARENT step (with conditionalSteps populated).
				// Push it back so the checkbox and "Create Email" button reflect the new state.
				if (result) {
					updateStep?.(step.id, () => result as Api.IAutomationStep);
				}
			} else {
				setHasFallbackEmailChecked(false);
				const conditionalStepId = fallbackEmailAutomationStep?.id;
				if (!conditionalStepId) {
					console.error('[RemoveConditionalStep] No conditional step ID found');
					return;
				}
				logEvent('RemoveConditionalStep', {
					parentStepId: step?.id,
					stepId: conditionalStepId,
					type: fallbackEmailAutomationStep?._type,
				});
				await removeConditionalStepMutation.mutateAsync({
					templateId,
					conditionalStepId,
					stepType: Api.AutomationStepType.Email,
					impersonationContext,
				});
				// Remove the conditional step from local data
				updateStep?.(step.id, prev => {
					const { Error: _removed, ...rest } = prev.conditionalSteps || {};
					return { ...prev, conditionalSteps: rest };
				});
			}
			onStepChanged?.();
		} catch {
			// Revert optimistic checkbox on failure
			setHasFallbackEmailChecked(Boolean(fallbackEmailAutomationStep));
		}
	};

	return (
		<>
			<AutomationStepCardInputFieldError step={step as unknown as Api.IAutomationStep}>
				<TextCampaignEditor
					className={css(styleSheet.textInputArea)}
					contentState={contentEditorState}
					onBlur={onTextingContentEditorBlur}
					onContentStateChanged={onTextingContentEditorStateChanged}
					onFocus={onRequestClearErrorMessages}
					onLoad={onEditorLoaded}
					readOnly={readOnly}
					styles={[baseStyleSheet.textField, styleSheet.textingContentEditor]}
				/>
			</AutomationStepCardInputFieldError>
			<div className={css(baseStyleSheet.horizontalStack, styleSheet.textingAddOnContainer)}>
				<EmojiPickerButton onEmojiClick={onEmojiClicked} />
				<AttachInputButton
					multiple={true}
					onChange={onFilesInputChanged}
					type='file'
					disabled={addAttachmentMutation.isPending}
				/>
				{!readOnly && (
					<MergeFieldsDropdown
						editorRef={contentEditorRef}
						onInsert={onMergeFieldInserted}
						onInteractionStart={onMergeFieldsInteractionStart}
						onOpenChange={onMergeFieldsOpenChange}
						canSendFileLinks={canSendFileLinks}
					/>
				)}
				<span className={css(styleSheet.textingAddOnText)}>
					Add an emoji or attach a file under {Api.convertToMB(Api.MAX_FILE_SIZE_MB)}
				</span>
			</div>
			{trigger?._type === 'TextingCampaignAutomationTrigger' && (
				<div className={css(styleSheet.textingCanSpam)}>
					<div className={css(styleSheet.textingCanSpamBubble)}>
						{'If you\'d prefer not to hear from me by text message, please reply back with "Stop".'}
					</div>
					<div className={css(styleSheet.textingCanSpamBubbleDetails)}>*Required by CAN-SPAM</div>
				</div>
			)}
			{renderBeforeFallbackEmail}
			<Checkbox
				checked={hasFallbackEmailChecked}
				className={css(styleSheet.textingFallbackCheckbox)}
				id={`texting-step-send-email-instead-${step.id}`}
				onChange={onFallbackToEmailCheckboxChanged}
			>
				<span>If contact does not have a mobile number, send email instead.</span>
			</Checkbox>
			<div className={css(baseStyleSheet.verticalStack, styleSheet.textingFallbackSettings)}>
				<div>Enter backup email content:</div>
				<button
					className={css(
						styleSheet.emailCta,
						!fallbackEmailAutomationStep?.content ? styleSheet.emailCreateButton : styleSheet.emailEditButton
					)}
					disabled={!fallbackEmailAutomationStep}
					onClick={onShowEmailFallbackComposerClicked}
				>
					<span>{`${fallbackEmailAutomationStep?.content ? 'Preview / Edit' : 'Create'} Email`}</span>
				</button>
				{showingEmailFallbackComposer && templateId ? (
					<EditAutomationStepEmailComposerModal
						automationTemplateId={templateId}
						step={fallbackEmailAutomationStep}
						onRequestClose={onEmailFallbackComposerModalRequestClose}
						onStepChanged={onStepChanged}
						onAttachmentRemoved={data => {
							Object.assign(fallbackEmailAutomationStep, data);
						}}
						readonly={readOnly}
						syncSubjectWithStep
						impersonationContext={impersonationContext}
						defaultInitialContent={Api.createRawRichTextContentStateWithText(
							getRichContentMessageForTextingStep(userSession, trigger, step.schedule)
						)}
					/>
				) : null}
			</div>
			<ConfirmationDialog
				icon={<WarningIcon />}
				modalProps={{
					isOpen: showingSizeLimitWarning,
					onRequestClose: onCloseSizeWarning,
				}}
				title='Max File Size Exceeded'
			>
				<p className={css(styleSheet.sizeWarning)}>
					Reduce your total file size below {Api.convertToMB(Api.MAX_FILE_SIZE_MB)} and resend.
				</p>
			</ConfirmationDialog>
		</>
	);
};
